import React, { FC, ReactElement } from 'react'
import { message, Spin, Typography, Card, Space, Row } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiCall } from '../../../../api/apiCall'
import { FileUploader } from '../../../Shared/FileUploader/FileUploader'
import { AttachmentRecord } from '../../../Shared/FileUploader/types'
import { OriginalDocumentsProps } from '../types'
import { Requests, ReactQuery } from 'api'
import { MESSAGES } from '../constants'
import { handleDownload } from '../helpers'
import styles from '../styles.module.scss'

export const OriginalDocuments: FC<OriginalDocumentsProps> = ({ orderId, currentUserId }): ReactElement => {
  const queryClient = useQueryClient()
  const attachmentsRequest = Requests.attachments({ orderId })
  const { data: attachmentsData, isLoading } = useQuery({
    queryKey: [attachmentsRequest.type, orderId],
    queryFn: () => ReactQuery.fetchResources(attachmentsRequest)
  })

  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: number) => {
      await apiCall.delete(Requests.attachment({ id: attachmentId, orderId }).path)
      return attachmentId
    },
    onSuccess: (attachmentId) => {
      queryClient.setQueryData(
        [attachmentsRequest.type, orderId],
        (prev: { resources: AttachmentRecord[] } | undefined) => ({
          ...prev,
          resources: (prev?.resources ?? []).filter((a) => a.id !== attachmentId)
        })
      )
    },
    onError: () => {
      message.error(MESSAGES.DELETE_ERROR)
    }
  })

  const handleDelete = async (attachment: AttachmentRecord) => {
    await deleteMutation.mutateAsync(attachment.id)
  }

  const handleUploadComplete = (newAttachment: AttachmentRecord) => {
    queryClient.setQueryData(
      [attachmentsRequest.type, orderId],
      (prev: { resources: AttachmentRecord[] } | undefined) => ({
        ...prev,
        resources: [...(prev?.resources ?? []), newAttachment]
      })
    )
  }

  const allAttachments = attachmentsData?.resources ?? []
  const nonScanbackAttachments = allAttachments.filter((a: AttachmentRecord) => !a.uploadedByNotary)

  return (
    <Card>
      <Space>
        <Typography.Text type="secondary">
          <FileTextOutlined className={styles.headerIcon} />
        </Typography.Text>
        <Typography.Title level={4}>{MESSAGES.UPLOAD_DOCUMENT_TITLE}</Typography.Title>
      </Space>

      {isLoading ? (
        <Row justify="center">
          <Spin />
        </Row>
      ) : (
        <FileUploader
          presignedPostPath={Requests.attachmentPresignedPost({ orderId }).path}
          confirmPath={Requests.attachments({ orderId }).path}
          multiple={true}
          fileList={nonScanbackAttachments}
          currentUserId={currentUserId}
          onUploadComplete={handleUploadComplete}
          onDownload={(attachment) => handleDownload(attachment, orderId)}
          onRemove={handleDelete}
        />
      )}
    </Card>
  )
}

export default OriginalDocuments
